/*
 * LW (2026-10-07): give every Globo form submission its context, and measure the form.
 * - Keeps the last tagged visit's UTMs for 30 days (gclid/fbclid/outside referrer when untagged), and that
 *   visit's Google click ID (gclid/gbraid/wbraid), so a lead sent from a later page still carries it to Odoo
 *   and writes them into Globo's hidden UTM fields, plus Pack / Pack choice / Landing page /
 *   Referrer hidden fields when a form has them (matched by label).
 * - Adds lw_ref (outside referrer hostname of the attributed visit) to the submitted page URL (Odoo lw_referrer).
 * - Adds lw_fbp / lw_fbc (Meta browser + click IDs, only if the pixel set them or an fbclid was seen) to the
 *   submitted page URL, so Odoo can match outcomes back to Meta (card 653).
 * - Rewrites the hidden page URL at submit so it carries the chosen pack
 *   (lw_pack=48pc), the page version (lw_view=product.v7-…) and the UTMs.
 * - Form block (blocks/lw-mockup-form: <div class="lw-form" data-hide-intent data-intent="…">):
 *   data-hide-intent hides the repeated "What are you interested in?" question and answers it
 *   (data-intent, else Bulk Package; Custom Quote when the visitor used a custom-quote link,
 *   which shows it again). data-intent alone preselects that answer and keeps it visible.
 * - data-autostart: opens the form's fields on load (Globo's step 1 is only a button).
 * - Events (Meta custom, GA4/gtag, dataLayer, Shopify customer events):
 *   pack_form_view, pack_form_start (first field tapped), pack_form_submit (success).
 *   Custom names on purpose, so they never double-count an existing Meta "Lead".
 */
(() => {
  // Attribution: the last tagged visit (or, failing that, the first outside referrer) within 30 days.
  // Stored in localStorage (sessionStorage as a fallback); when storage is blocked (some in-app
  // browsers), the current URL still counts and Globo's own value is never blanked.
  const KEY = 'lw_utm';
  const TTL = 30 * 864e5;
  const UTM = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
  const GCLICK = ['gclid', 'gbraid', 'wbraid'];
  const clean = (v) => (v && !/\{\{|%7B%7B/i.test(v) ? String(v).trim().slice(0, 200) : '');
  const stores = [() => localStorage, () => sessionStorage];
  const load = () => {
    for (const st of stores) {
      try {
        const o = JSON.parse(st().getItem(KEY) || 'null');
        if (o && (!o.ts || Date.now() - o.ts < TTL)) return o;
      } catch (e) {}
    }
    return null;
  };
  const save = (o) => { o.ts = Date.now(); stores.forEach((st) => { try { st().setItem(KEY, JSON.stringify(o)); } catch (e) {} }); };
  const qs = new URLSearchParams(location.search);
  const fresh = {};
  UTM.forEach((k) => { const v = clean(qs.get(k)); if (v) fresh[k] = v; });
  GCLICK.forEach((k) => { const v = clean(qs.get(k)); if (v) fresh[k] = v; });
  if (!fresh.utm_source && GCLICK.some((k) => fresh[k])) Object.assign(fresh, { utm_source: 'google', utm_medium: fresh.utm_medium || 'cpc' });
  if (!fresh.utm_source && qs.get('fbclid')) Object.assign(fresh, { utm_source: 'facebook', utm_medium: fresh.utm_medium || 'social' });
  // fbclid + first-seen time, so a missing _fbc cookie can be rebuilt as fb.1.<ms>.<fbclid> (Meta's format).
  const FBC_KEY = 'lw_fbclid';
  if (qs.get('fbclid')) { try { localStorage.setItem(FBC_KEY, JSON.stringify({ id: qs.get('fbclid'), ts: Date.now() })); } catch (e) {} }
  const fbIds = () => {
    const ck = (n) => (document.cookie.match(new RegExp('(?:^|; )' + n + '=([^;]*)')) || [])[1] || '';
    let fbc = ck('_fbc');
    if (!fbc) {
      try {
        const f = JSON.parse(localStorage.getItem(FBC_KEY) || 'null');
        if (f && f.id && Date.now() - f.ts < TTL) fbc = 'fb.1.' + f.ts + '.' + f.id;
      } catch (e) {}
    }
    // Only IDs the Meta pixel already set (or a click ID it would have used); nothing new is created.
    return { lw_fbp: ck('_fbp'), lw_fbc: fbc };
  };
  const ref = (() => { try { const r = new URL(document.referrer); return r.hostname === location.hostname || /shopify|shop\.app/.test(r.hostname) ? null : r; } catch (e) { return null; } })();
  const stored = load();
  if (fresh.utm_source || fresh.utm_campaign) {
    save(Object.assign({}, fresh, { landing: location.pathname, referrer: ref ? ref.hostname : '' }));
  } else if (ref && !stored) {
    const h = ref.hostname.replace(/^www\./, '');
    const se = h.match(/(^|\.)(google|bing|duckduckgo|yahoo|ecosia)\./);
    const so = h.match(/(^|\.)(facebook|instagram|linkedin|tiktok|pinterest|reddit|youtube)\./) || (/^(l|lm|m)\.facebook\.com$/.test(h) && [0, 0, 'facebook']);
    save({ utm_source: se ? se[2] : so ? so[2] : h, utm_medium: se ? 'organic' : so ? 'social' : 'referral', landing: location.pathname, referrer: h });
  }
  const utm = () => {
    const o = Object.assign({}, load() || {}, fresh);
    try { // Globo's own copy (read from the URL it was loaded on) fills any gap.
      const g = window.Globo?.FormBuilder?.utm || {};
      UTM.forEach((k) => { const v = clean(g[k.slice(4)]); if (!o[k] && v) o[k] = v; });
    } catch (e) {}
    // No stored click ID: the Google tag's own _gcl_aw cookie (GCL.<time>.<gclid>) when it set one.
    if (!GCLICK.some((k) => o[k])) {
      const m = document.cookie.match(/(?:^|; )_gcl_aw=GCL\.\d+\.([^;]+)/);
      if (m) o.gclid = clean(decodeURIComponent(m[1]));
    }
    return o;
  };

  const ctx = () => ({
    pack: document.querySelector('variant-picker input:checked')?.value || '',
    page_version: (window.lwPage && window.lwPage.template) || '',
    page: location.pathname,
  });

  const track = (name, extra) => {
    const params = Object.assign(ctx(), extra || {});
    try { window.fbq && window.fbq('trackCustom', name, params); } catch (e) {}
    try { window.gtag && window.gtag('event', name, params); } catch (e) {}
    try { (window.dataLayer = window.dataLayer || []).push(Object.assign({ event: name }, params)); } catch (e) {}
    try { window.Shopify?.analytics?.publish?.(name, params); } catch (e) {}
  };

  // Hidden fields Morgan adds in Globo, matched by their label (case-insensitive):
  //   Pack = product title, Pack choice = selected option, Landing page / Referrer = first page / outside site.
  const extras = (form) => {
    const c = ctx();
    const u = utm();
    return [
      [/^pack$/i, form.closest('[data-pack]')?.dataset.pack || ''],
      [/^pack\s*choice$/i, c.pack],
      [/^landing\s*page$/i, u.landing || location.pathname],
      [/^referrer$/i, u.referrer || ''],
    ];
  };

  const fill = (form) => {
    const u = utm();
    // Never overwrite with an empty value: Globo resolves its own placeholder from the URL.
    const put = (i, v) => { if (i && v) i.value = v; };
    const set = (n, v) => { const i = form.querySelector(`[name="${n}"]`); if (i && v && (!i.value || i.value.includes('{{'))) i.value = v; };
    // Globo hidden fields carry a placeholder like {{-utm.medium-}}: fill each by what it asks for, not by
    // its name (forms number their hidden fields differently, e.g. 129158 starts at hidden-2).
    let mapped = false;
    form.querySelectorAll('input[type=hidden]').forEach((i) => {
      const m = i.value.match(/\{\{-utm\.(\w+)-\}\}/);
      if (m) i.dataset.lwUtm = m[1];
      if (!i.dataset.lwUtm) return;
      mapped = true;
      put(i, u['utm_' + i.dataset.lwUtm]);
    });
    if (!mapped && !form.querySelector('[data-lw-utm]')) { set('hidden-1', u.utm_source); set('hidden-2', u.utm_medium); set('hidden-3', u.utm_campaign); }
    let labels = {};
    try { labels = JSON.parse(form.querySelector('[name="_keyLabel"]')?.value || '{}'); } catch (e) {}
    const ex = extras(form);
    Object.entries(labels).forEach(([key, label]) => {
      const hit = ex.find(([re]) => re.test(String(label).trim()));
      if (hit) put(form.querySelector(`input[type=hidden][name="${key}"]`), hit[1]);
    });
    const h = form.querySelector('[name="page[href]"]');
    if (h) {
      const url = new URL(location.href);
      const c = ctx();
      if (c.pack) url.searchParams.set('lw_pack', c.pack);
      if (c.page_version) url.searchParams.set('lw_view', c.page_version);
      UTM.forEach((k) => { if (u[k] && !url.searchParams.has(k)) url.searchParams.set(k, u[k]); });
      // The stored Google click ID, unless this URL already has one (Odoo reads gclid/gbraid/wbraid here).
      if (!GCLICK.some((k) => url.searchParams.has(k))) GCLICK.forEach((k) => { if (u[k]) url.searchParams.set(k, u[k]); });
      Object.entries(fbIds()).forEach(([k, v]) => { if (v) url.searchParams.set(k, v); });
      // lw_ref: the outside site (hostname only, never its path or query) that brought the attributed visit,
      // else this visit's own outside referrer. For Odoo's lw_referrer (card 653).
      const r = u.referrer || (ref ? ref.hostname : '');
      if (r && !url.searchParams.has('lw_ref')) url.searchParams.set('lw_ref', r.replace(/^www\./, ''));
      h.value = url.toString();
    }
  };

  const setIntent = (form, want, show) => {
    const sel = [...form.querySelectorAll('select')].find((s) => [...s.options].some((o) => /quote|package|mockup/i.test(o.text)));
    if (!sel) return;
    const opt = [...sel.options].find((o) => o.text.toLowerCase().includes(want));
    if (opt) { sel.value = opt.value; sel.dispatchEvent(new Event('change', { bubbles: true })); }
    const wrap = sel.closest('.globo-form-control');
    if (wrap) wrap.style.display = show ? '' : 'none';
  };
  window.lwFormContext = { fill: (f) => fill(f), ctx, track };
  window.lwSetIntent = (want) => document.querySelectorAll('.lw-inline-form .globo-formbuilder').forEach((f) => setIntent(f, want.toLowerCase(), true));

  const visible = (el) => { const r = el.getBoundingClientRect(); return r.width > 0 && r.height > 0 && getComputedStyle(el).visibility !== 'hidden'; };
  const wire = (gf) => {
    if (gf.__lwWired || !gf.querySelector('form')) return false;
    gf.__lwWired = true;
    const host = gf.closest('.lw-form, .lw-inline-form');
    const want = (host?.dataset.intent || '').toLowerCase();
    if (host?.hasAttribute('data-hide-intent')) setIntent(gf, want || 'bulk package', false);
    else if (want) setIntent(gf, want, true);
    if (host?.hasAttribute('data-autostart')) {
      // Globo binds its step buttons a moment after rendering: retry until the fields show.
      let tries = 0;
      const open = () => {
        const fields = [...gf.querySelectorAll('input[type=text],input[type=email],textarea')].filter(visible);
        if (fields.length) return;
        const next = gf.querySelector('.action.next');
        if (next && visible(next)) {
          // Globo focuses/scrolls to the first field after a step change; opening on load must
          // not move the visitor away from the top of the page.
          const pw = document.querySelector('.page-wrapper');
          const keep = { y: window.scrollY, p: pw ? pw.scrollTop : 0 };
          const restore = () => {
            if (gf.contains(document.activeElement)) document.activeElement.blur();
            window.scrollTo(0, keep.y);
            if (pw) pw.scrollTop = keep.p;
          };
          next.click();
          [0, 50, 150, 350, 700].forEach((t) => setTimeout(restore, t));
        }
        if (++tries < 8) setTimeout(open, 600);
      };
      setTimeout(open, 400);
    }
    let started = false;
    gf.addEventListener('focusin', (e) => {
      if (started || !e.target.matches('input,textarea,select')) return;
      started = true; track('pack_form_start');
    });
    // Fill context just before Globo collects the values (capture phase, before its handlers).
    gf.addEventListener('click', (e) => { if (e.target.closest('.action.submit, .action.next, button[type=submit]')) fill(gf); }, true);
    gf.addEventListener('submit', () => fill(gf), true);
    // Success: Globo fills its congrats message / success state.
    let sent = false;
    new MutationObserver(() => {
      if (sent) return;
      const msg = gf.querySelector('.wizard__congrats-message, .message.success, .globo-form-message.success');
      if (msg && msg.textContent.trim() && visible(msg)) { sent = true; track('pack_form_submit'); }
    }).observe(gf, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ['class', 'style'] });
    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver((ents) => { if (ents.some((x) => x.isIntersecting)) { track('pack_form_view'); io.disconnect(); } }, { threshold: 0.4 });
      io.observe(gf);
    }
    return true;
  };
  // Globo renders its forms asynchronously: poll briefly (cheap) instead of observing every
  // DOM mutation on the page.
  let polls = 0;
  const scan = () => {
    const forms = document.querySelectorAll('.globo-formbuilder');
    forms.forEach(wire);
    const pending = [...forms].some((f) => !f.__lwWired);
    if ((pending || !forms.length) && ++polls < 40) setTimeout(scan, 500);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', scan); else scan();
})();
